/*
 * Field: label, hint and error around one control, wiring ids and aria for it. Controls in this kit (Input,
 * PasswordInput, Textarea, Select, Combobox, OtpInput) read the field context, so nothing else is needed:
 *
 *   <Field label={t('auth:fields.email')} hint={…} error={fieldError(errors.email)} required>
 *     <Input type="email" autoComplete="email" {...register('email')} />
 *   </Field>
 *
 * `error` is the already translated message (see fieldError in src/lib/forms.ts).
 */
import { createContext, useContext, useId, type ReactNode } from 'react'
import { WarningCircle } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'

type FieldCtx = { id: string; describedBy?: string; invalid: boolean; required?: boolean }
const Ctx = createContext<FieldCtx | null>(null)

/** For custom controls: `const f = useFieldControl(); <input id={f?.id} aria-describedby={f?.describedBy} …/>` */
export function useFieldControl() {
  return useContext(Ctx)
}

/** Props a control spreads to join its Field (id, aria-invalid, aria-describedby, required). */
export function useFieldProps(props: {
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling'
  required?: boolean
}) {
  const f = useContext(Ctx)
  return {
    id: props.id ?? f?.id,
    'aria-describedby': [props['aria-describedby'], f?.describedBy].filter(Boolean).join(' ') || undefined,
    'aria-invalid': props['aria-invalid'] ?? (f?.invalid ? true : undefined),
    required: props.required ?? f?.required,
  }
}

export function Field({
  label,
  hint,
  error,
  required,
  optional,
  labelAction,
  children,
  className,
  id: idProp,
}: {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  /** shows "(optional)" next to the label */
  optional?: boolean
  /** a small link or button on the end side of the label row (e.g. "Forgot password?") */
  labelAction?: ReactNode
  children: ReactNode
  className?: string
  id?: string
}) {
  const { t } = useTranslation('common')
  const auto = useId()
  const id = idProp ?? `f${auto.replace(/:/g, '')}`
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined
  return (
    <Ctx.Provider value={{ id, describedBy, invalid: Boolean(error), required }}>
      <div className={cn('flex flex-col gap-1.5', className)}>
        {label || labelAction ? (
          <div className="flex items-baseline justify-between gap-3">
            {label ? (
              <label htmlFor={id} className="text-13 font-semibold text-ink">
                {label}
                {optional ? <span className="ms-1 font-normal text-subtle">({t('ui.optional')})</span> : null}
              </label>
            ) : (
              <span />
            )}
            {labelAction}
          </div>
        ) : null}
        {children}
        {error ? (
          <p id={errorId} role="alert" className="flex items-start gap-1.5 text-13 text-danger-700">
            <WarningCircle className="mt-0.5 size-4 shrink-0" weight="fill" aria-hidden="true" />
            <span>{error}</span>
          </p>
        ) : hint ? (
          <p id={hintId} className="text-13 text-subtle">
            {hint}
          </p>
        ) : null}
      </div>
    </Ctx.Provider>
  )
}
